import React, { useState, useEffect } from "react"
import { useAuth } from "../../contexts/AuthContext"
import { lmsConnectionsApi, coursesApi } from "../../lib/api"
import SettingsTabs from "./SettingsTabs"
import LmsConnections from "./LmsConnections"
import CourseSettings from "./CourseSettings"
import InviteParentModal from "./InviteParentModal"
import InvitationsList from "./InvitationsList"
import LinkedParentsList from "./LinkedParentsList"
import LinkedStudentsList from "./LinkedStudentsList"
import ProfileSettings from "./ProfileSettings"
import NotificationSettings from "./NotificationSettings"
import McpSettings from "./McpSettings"
import ChangePasswordSettings from "./ChangePasswordSettings"

// Tab icons as components
const ProfileIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
  </svg>
)

const NotificationsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
  </svg>
)

const ConnectionsIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
  </svg>
)

const FamilyIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
  </svg>
)

const Settings = () => {
  const { user, isStudent, isParent } = useAuth()
  const [connections, setConnections] = useState([])
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [invitationsRefresh, setInvitationsRefresh] = useState(0)
  const [activeTab, setActiveTab] = useState("profile")

  // Define tabs based on user type
  const getTabs = () => {
    const baseTabs = [
      { id: "profile", label: "Profile", icon: <ProfileIcon /> },
      { id: "notifications", label: "Notifications", icon: <NotificationsIcon /> },
      { id: "assistants", label: "AI assistants", icon: <ConnectionsIcon /> },
    ]

    if (isStudent) {
      return [
        ...baseTabs,
        { id: "connections", label: "Connections", icon: <ConnectionsIcon /> },
        { id: "family", label: "Family", icon: <FamilyIcon /> },
      ]
    }

    if (isParent) {
      return [
        ...baseTabs,
        { id: "family", label: "Students", icon: <FamilyIcon /> },
      ]
    }

    return baseTabs
  }

  const tabs = getTabs()

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const [connectionsData, coursesData] = await Promise.all([
        lmsConnectionsApi.list(),
        coursesApi.list().catch(() => ({ courses: [] })),
      ])
      setConnections(connectionsData.lms_connections || [])
      setCourses(coursesData.courses || [])
    } catch (error) {
      console.error("Failed to fetch settings:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleCourseToggle = async (courseId, hidden) => {
    await coursesApi.update(courseId, { hidden })
    setCourses((prev) =>
      prev.map((c) => (c.id === courseId ? { ...c, hidden } : c))
    )
  }

  const handleCourseAdd = async (data) => {
    const response = await coursesApi.create(data)
    setCourses((prev) => [...prev, response.course])
  }

  const handleCourseUpdate = async (courseId, data) => {
    const response = await coursesApi.update(courseId, data)
    setCourses((prev) =>
      prev.map((c) => (c.id === courseId ? response.course : c))
    )
  }

  const handleCourseDelete = async (courseId) => {
    await coursesApi.delete(courseId)
    setCourses((prev) => prev.filter((c) => c.id !== courseId))
  }

  const handleConnectionChange = () => {
    fetchSettings()
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 bg-gray-200 rounded-lg animate-pulse"></div>
        <div className="h-12 bg-gray-200 rounded-xl animate-pulse"></div>
        <div className="animate-pulse h-64 bg-gray-200 rounded-xl"></div>
      </div>
    )
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return (
          <div className="space-y-6">
            <ProfileSettings />
            <ChangePasswordSettings />
          </div>
        )

      case "assistants":
        return <McpSettings />

      case "notifications":
        return <NotificationSettings />

      case "connections":
        return (
          isStudent && (
            <div className="space-y-6">
              <LmsConnections onConnectionChange={handleConnectionChange} />
              <CourseSettings
                courses={courses}
                onToggle={handleCourseToggle}
                onAdd={handleCourseAdd}
                onUpdate={handleCourseUpdate}
                onDelete={handleCourseDelete}
              />
            </div>
          )
        )

      case "family":
        if (isStudent) {
          return (
            <>
              <section className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Parent/Guardian Access</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Invite parents or guardians to view your assignment dashboard
                    </p>
                  </div>
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm"
                  >
                    Invite Parent
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                      </svg>
                      Sent Invitations
                    </h3>
                    <InvitationsList refreshTrigger={invitationsRefresh} />
                  </div>

                  <div className="pt-6 border-t">
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                      </svg>
                      Linked Parents
                    </h3>
                    <LinkedParentsList />
                  </div>
                </div>
              </section>

              <InviteParentModal
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                onSuccess={() => setInvitationsRefresh((n) => n + 1)}
              />
            </>
          )
        }

        if (isParent) {
          return (
            <section className="bg-white rounded-xl shadow-sm border p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Linked Students</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Students you're monitoring. Ask students to send you an invitation to link their account.
                </p>
              </div>
              <LinkedStudentsList />
            </section>
          )
        }

        return null

      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      </div>

      {/* Tabs Navigation */}
      <SettingsTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab Content */}
      <div className="mt-6">
        {renderTabContent()}
      </div>
    </div>
  )
}

export default Settings
